import { makeAutoObservable, runInAction } from "mobx";
import { RootStore } from "../../shared/stores/RootStore";
import {
  buildEmptyUpdateSimulationWay,
  CreateSimulationWay,
  SimulationWay,
  simulationWayToUpdate,
  UpdateSimulationWay,
} from "../models/SimulationWay";
import { SimulationWayApi } from "../services/SImulationWayApi";
import {
  CreateOrUpdateSimulationLane,
  SimulationLane,
} from "../models/SimulationLane";
import { HlaWayApi } from "../services/HlaWayApi";

export class SimulationStore {
  rootStore: RootStore;

  selectedSimulationWay?: SimulationWay;
  isEditing = false;
  simulatedHlaWayIds = new Set<string>();
  isSimulationOpen = false;
  simulationWayId?: string;

  form: UpdateSimulationWay = buildEmptyUpdateSimulationWay();
  defaultToFrontFacing = true;
  dirtyIds = new Set<string>();
  savingChanges = false;
  loadingSimulation = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  resetState() {
    runInAction(() => {
      this.savingChanges = false;
      this.dirtyIds = new Set();
      this.selectedSimulationWay = undefined;
      this.form = buildEmptyUpdateSimulationWay();
    });
  }

  async createSimulated(body: CreateSimulationWay) {
    const { data } = await SimulationWayApi.createSimulated(body);

    runInAction(() => {
      this.selectedSimulationWay = data;
      this.form = simulationWayToUpdate(data);
      this.isSimulationOpen = true;
      this.isEditing = true;
    });

    return data;
  }

  async loadSimulatedHlaWayIds(surveyId: string) {
    const { data } = await SimulationWayApi.listHlaWayIdsBySurvey(surveyId);

    runInAction(() => {
      this.simulatedHlaWayIds = new Set(data);
    });
  }

  selectSimulationWayId(simulationWayId: string) {
    runInAction(() => {
      this.simulationWayId = simulationWayId;
    });
  }

  async getSimulaions(hlaWayId: string) {
    try {
      const { data } = await HlaWayApi.getSimulation(hlaWayId);

      runInAction(() => {
        this.selectedSimulationWay = data;
        this.form = simulationWayToUpdate(data);

        if (data.verified) {
          this.selectedSimulationWay = data;
          this.isSimulationOpen = true;
          this.isEditing = false;
        }
      });

      return data;
    } catch (error: any) {
      runInAction(() => {
        this.selectedSimulationWay = undefined;
        this.form = buildEmptyUpdateSimulationWay();
      });

      if (error?.response?.status === 404 || error?.response?.status === 500)
        return null;
      throw error;
    }
  }

  getSelectedLane(): SimulationLane | undefined {
    const frontFacingLane = this.selectedSimulationWay?.frontFacingLane;
    const backFacingLane = this.selectedSimulationWay?.backFacingLane;

    if (!this.defaultToFrontFacing && backFacingLane) {
      return backFacingLane;
    }

    return frontFacingLane ?? backFacingLane ?? undefined;
  }

  getSelectedFormLane(): CreateOrUpdateSimulationLane | null {
    if (this.defaultToFrontFacing) {
      return this.form.frontFacingLane;
    }
    return this.form.backFacingLane;
  }

  selectSimulationLane = (frontFacing: boolean) => {
    runInAction(() => {
      this.defaultToFrontFacing = frontFacing;
    });
  };

  code = (callback: (prev: UpdateSimulationWay) => UpdateSimulationWay) => {
    runInAction(() => {
      this.form = callback(this.form);

      if (this.selectedSimulationWay?.id) {
        this.markAsDirty(this.selectedSimulationWay.id);
      }
    });
  };

  verify() {
    runInAction(() => {
      this.form = { ...this.form, verified: true };

      if (this.selectedSimulationWay) {
        this.selectedSimulationWay.verified = true;
      }
    });
  }

  async saveChanges({ finish = false } = {}) {
    if (!this.selectedSimulationWay?.id) return;

    runInAction(() => {
      this.savingChanges = true;
    });

    try {
      const { data: updatedWay } = await SimulationWayApi.update(
        this.selectedSimulationWay.id,
        this.form,
      );

      runInAction(() => {
        this.selectedSimulationWay = updatedWay;
        this.form = simulationWayToUpdate(updatedWay);

        this.isSimulationOpen = true;
        this.isEditing = !finish;

        this.dirtyIds.delete(updatedWay.id);
      });
    } finally {
      runInAction(() => {
        this.savingChanges = false;
      });
    }
  }

  private markAsDirty = (wayId: string) => {
    runInAction(() => {
      this.dirtyIds.add(wayId);
    });
  };

  openComparison() {
    runInAction(() => {
      this.isSimulationOpen = true;
      this.isEditing = false;
    });
  }
  editSimulation() {
    runInAction(() => {
      if (this.selectedSimulationWay) {
        this.form = simulationWayToUpdate(this.selectedSimulationWay);
      }
      this.isSimulationOpen = true;
      this.isEditing = true;
    });
  }
  finishSimulation() {
    runInAction(() => {
      this.isSimulationOpen = true;
      this.isEditing = false;
    });
  }

  backToHla() {
    runInAction(() => {
      this.form = buildEmptyUpdateSimulationWay();
      this.defaultToFrontFacing = true;
      this.isSimulationOpen = false;
      this.isEditing = false;
    });
  }

  clearSelectedSimulation() {
    runInAction(() => {
      this.selectedSimulationWay = undefined;
      this.defaultToFrontFacing = true;
      this.form = buildEmptyUpdateSimulationWay();
      this.isEditing = false;
      this.isSimulationOpen = false;
      this.resetState();
    });
  }

  async delete(simulationWayId: string) {
    await SimulationWayApi.deleteSimulation(simulationWayId);
    this.clearSelectedSimulation();
  }
}
